import React from "react";
import { usePost } from "../../../../api/useCall";
import ModalForm from "@ui/Modal/Form";
import InputV2 from "@ui/Input/InputV2";
import t from "../../../../i18n";
import type { Setup } from "../../../../api/useSetups";
import { useSetups } from "../../../../api/useSetups";
import useInput from "@ui/lib/hooks/useInput";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { useNavigate } from "react-router";
import useToasts from "@ui/Toast/useToasts";
import { useMutateBody } from "../../../../api/useQuery";
import { toPath } from "../../../../router/helpers/path";
import { formatDateToYYYYMMDD } from "diagnostics-lib/dates";
interface P {
  projectId: string;
  setup: Setup;
  onRequestClose: () => void;
}

const DuplicateModalForm = ({ projectId, setup, onRequestClose }: P) => {
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const setups = useSetups(projectId);
  const duplicateSetup = usePost("/projects/:projectId(uuid)/setups/:setupId(uuid)/duplicate");
  const mutateSetups = useMutateBody("/projects/:projectId(uuid)/setups", {
    routeParams: { projectId },
  });
  const nameInput = useInput(t("setups.modal.duplicate.duplicated_name", { name: setup.name }));
  const inputNameError = React.useMemo(() => {
    if (nameInput.value === "") {
      return t("setups.modal.create_or_edit.input.name.error.cannot_be_empty");
    }
    if (setups.some(s => s.name === nameInput.value.trim())) {
      return t("setups.modal.create_or_edit.input.name.error.name_already_exists");
    }
    return;
  }, [setups, nameInput.value]);

  const descriptionInput = useInput(setup.description || "");
  const setupStartDates = setups
    .flatMap(setup => (setup.startDate ? [new Date(setup.startDate)] : []))
    .sort((a, b) => a.getTime() - b.getTime())
    .map(date => {
      const newDate = new Date(date.getTime());
      newDate.setDate(newDate.getDate() + 1);
      return formatDateToYYYYMMDD(newDate);
    });
  const dateInput = useInput(
    formatDateToYYYYMMDD(
      new Date(setupStartDates[setupStartDates.length - 1] ?? Date.now()),
    ).toString(),
  );
  const startedDateError: string | undefined = React.useMemo(() => {
    if (dateInput.value === "")
      return t("setups.modal.create_or_edit.input.application_date.error.cannot_be_empty");
    if (
      new Date(dateInput.value).getTime() <
      new Date(setupStartDates[setupStartDates.length - 1] as string).getTime()
    )
      return t("setups.modal.create_or_edit.input.startDate.error.should_be_greater");
    return;
  }, [dateInput, setupStartDates]);

  const handleSubmit = React.useCallback(async () => {
    if (inputNameError || startedDateError) return false;

    const res = await duplicateSetup({
      routeParams: { projectId, setupId: setup.id },
      body: {
        name: nameInput.value,
        description: descriptionInput.value,
        startDate: new Date(dateInput.value),
      },
    });

    mutateSetups(cache => [...cache, res.body]);
    void navigate(
      toPath("/projects/:projectId/setup/:setupId", { projectId, setupId: res.body.id }),
      {
        replace: true,
      },
    );
    addToast(
      "success",
      t("setup.modal.duplicate_setup.toast.success", {
        name: setup.name,
      }),
    );

    return true;
  }, [
    inputNameError,
    startedDateError,
    duplicateSetup,
    projectId,
    setup.id,
    nameInput.value,
    descriptionInput.value,
    dateInput.value,
    navigate,
    addToast,
    setup.name,
    mutateSetups,
  ]);

  return (
    <ModalForm
      onSubmit={!inputNameError && !startedDateError ? () => handleSubmit() : false}
      onRequestClose={onRequestClose}
    >
      <InputV2
        label={t("setups.modal.duplicate_setup.title.name")}
        required
        error={inputNameError}
        placeholder={t("setups.modal.duplicate_setup.title.name.placeholder")}
        {...nameInput}
        revertable={false}
      />
      <InputV2
        label={t("setups.modal.duplicate_setup.title.description")}
        required
        placeholder={t("setups.modal.duplicate_setup.title.description.placeholder")}
        {...descriptionInput}
        revertable={false}
      />
      <InputV2
        label={t("setups.modal.duplicate_setup.title.effective_start_date")}
        required
        type="date"
        {...dateInput}
        min={setupStartDates[setupStartDates.length - 1]}
        error={startedDateError}
        revertable={false}
      />
      <AdditionnalMessage
        status="WARNING"
        text={t("setups.modal.duplicate_setup.message.warning.reset_simulation_diagnostic")}
      />
    </ModalForm>
  );
};

export default React.memo(DuplicateModalForm);
